'use client';

import { useCallback, useMemo, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { useFormState } from 'react-dom';
import { v4 as uuid } from 'uuid';
import { useTranslations } from 'next-intl';
import { useFormSubmitEvent } from 'hooks/analytics/use-form-submit-event';
import useLocations, {
  Coordinates
} from 'app/[lang]/[esite]/(core)/_header/hooks/use-locations';
import { DrawerBody, useDrawer } from 'components/pcc-components';
import { DealerLocatorHeader } from './dealer-locator-header';
import { DealerLocatorSearchResults } from './dealer-locator-search-results';
import { DealerLocatorOptionsWrapper } from './dealer-locator-options-wrapper';
import { DealerLocatorSearchForm } from './dealer-locator-search-form';
import { DealerLocatorFiltersDrawer } from './dealer-locator-filters-drawer';
import { useDealerLocatorDrawer } from '../dealer-locator-drawer-context';
import { DealerLocatorFooter } from './dealer-locator-footer';
import {
  DEALER_NAME,
  DEFAULT_NUM_RESULTS,
  DISTANCE,
  OPTIONS,
  SELECTED_STORE_ID,
  SELECT_STORE,
  SORT_ORDER
} from './constants';
import { DEALER_LOCATOR_FORM_ID } from '@cat-ecom/pcc-components/src/components/dealer-locator/constants';
import { SELECT_STORE_INITIAL_STATE } from 'services/dealer-locator/constants';
import {
  Navigation,
  SelectStoreResponse,
  SelectedStore
} from 'services/dealer-locator/types';
import { selectStore } from 'services/dealer-locator/actions';
import { getFilteredAndOrderedDealerStores } from './utils';
import { StoreAddress } from '../types';
import { useHideMaps } from 'hooks/use-hide-maps';
import { usePathname, useSearchParams } from 'next/navigation';
import { setSessionStorage } from '@cat-ecom/common/utils/storageUtils';
import { CONSENT } from 'constants/storageConstants';

interface DealerLocatorContentProps {
  autoSearch?: boolean;
  isADP?: boolean;
  isModal?: boolean;
  isRegistration?: boolean;
  storeAddress?: StoreAddress;
  isWelcomeModal?: boolean;
  restrictedDealerStores?: string[];
}

export const DealerLocatorContent = ({
  autoSearch,
  isADP,
  isModal = false,
  isRegistration = false,
  storeAddress,
  isWelcomeModal,
  restrictedDealerStores
}: DealerLocatorContentProps) => {
  const t = useTranslations();
  const { isHidingMaps } = useHideMaps();
  const dealerLocatorDrawer = useDealerLocatorDrawer();
  const sortingOptions = useMemo(
    () => [
      { label: t('FYD_SORT_CLOSEST'), value: DISTANCE },
      { label: t('CAT_EMAIL_DEALERNAME'), value: DEALER_NAME }
    ],
    [t]
  );
  const storeFormMethods = useForm();
  const { fireEvent } = useFormSubmitEvent();

  const defaultFilterOptionsValues: boolean[] = useMemo(
    () => Array(10).fill(false),
    []
  );

  const filterMethods = useForm({
    defaultValues: {
      options: defaultFilterOptionsValues,
      sortOrder: sortingOptions[0]
    }
  });
  const filtersDrawer = useDrawer({
    id: `${
      dealerLocatorDrawer?.drawerProps?.id || 'blocks-dealer-locator-drawer'
    }-filters`,
    onClose: () => {
      shouldCloseEntireDL && dealerLocatorDrawer?.closeDrawer();
    },
    //@ts-expect-error ignoring the 'md' size error, as we need this size for the filters modal if used within our DL modal
    size: isModal ? 'md' : 'lg'
  });
  const {
    getLocations,
    locations,
    showInCountryToggle,
    unitOfMeasure,
    isLoading,
    isIdle,
    reset,
    isError,
    isExtendMapToggleChecked,
    setIsExtendMapToggleChecked
  } = useLocations(restrictedDealerStores ?? []);
  const [selectStoreState, selectStoreAction] = useFormState<
    SelectStoreResponse,
    SelectedStore
  >(selectStore, SELECT_STORE_INITIAL_STATE);
  /*  TODO - Input Validations - useSearchParams:The search param object should contain key value pairs where
  the value is of string type, not including reservered url special / unsafe characters like / \ & ? = : % + ' " ; < > { } [ ] @ #
  */
  const queryParams = useSearchParams();

  /*
    TODO - Input Validations - usePathName: string except the
    generalRestrictedCharacters (@\constants\securityConstants.ts)
  */
  const pathname = usePathname();
  const hasQueryParams = !!queryParams.size;
  const allParams = queryParams.toString();

  const [location, setLocation] = useState('');
  const [numDisplayed, setNumDisplayed] = useState(DEFAULT_NUM_RESULTS);
  const [isMapToggleChecked, setIsMapToggleChecked] = useState(false);
  const [filters, setFilters] = useState(null);
  const [areFiltersLoading, setAreFiltersLoading] = useState(false);
  const [shouldCloseEntireDL, setShouldCloseEntireDL] = useState(true);
  const [isSelectStoreError, setIsSelectStoreError] = useState(false);

  const {
    filteredAndOrderedVisibleDealerStores,
    allFilteredDealerStoresByDistance
  } = useMemo(
    () => getFilteredAndOrderedDealerStores(locations, numDisplayed, filters),
    [locations, numDisplayed, filters]
  );
  const isStoreLocationTandCFlag =
    dealerLocatorDrawer?.isStoreLocationTandCFlag ?? false;
  const showFilterErrorMessage =
    locations?.length && !filteredAndOrderedVisibleDealerStores.length;
  const showNoStoresMapErrorMessage =
    !isIdle && !isLoading && !locations?.length && !isError;

  const showNetworkErrorMessage = !isIdle && !isLoading && isError;

  const selectedId = storeFormMethods.getValues(SELECTED_STORE_ID);
  const selectedStore = locations?.find(
    ({ id }) => id.toString() === selectedId
  );
  const navigation: Navigation = {
    pathname,
    hasQueryParams,
    queryParams: allParams
  };

  const selectedStoreWithRegistration = {
    ...selectedStore,
    isRegistration,
    navigation
  };

  const selectStoreWithParams = selectStoreAction.bind(
    null,
    selectedStoreWithRegistration
  );

  const formId = `${uuid()}-${DEALER_LOCATOR_FORM_ID}`;

  const handleSubmit = () => {
    setSessionStorage(CONSENT, 'true');
    fireEvent({
      formName: SELECT_STORE,
      formContent: `${selectedStore?.dealerName}, ${selectedStore?.name}`
    });
  };

  const onSearchThisAreaClick = (center: Coordinates) => {
    storeFormMethods.resetField(SELECTED_STORE_ID);
    setNumDisplayed(DEFAULT_NUM_RESULTS);
    const locationText = `${center.lat}, ${center.lng}`;
    setLocation(locationText);
    getLocations(locationText);
  };

  const resetLocationsAndMapToggle = useCallback(() => {
    reset();
    setIsMapToggleChecked(false);
    setIsExtendMapToggleChecked(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reset]);

  const resetFilters = useCallback(() => {
    setFilters(null);
    setAreFiltersLoading(false);
    filterMethods.resetField(SORT_ORDER);
    filterMethods.resetField(OPTIONS);
  }, [filterMethods]);

  const resetStoreId = () => storeFormMethods.resetField(SELECTED_STORE_ID);

  return (
    <>
      <FormProvider {...storeFormMethods}>
        <DealerLocatorHeader
          isRegistration={isRegistration}
          isADP={isADP}
          isWelcomeModal={isWelcomeModal}
        />
        <DrawerBody>
          <div className="px-1">
            <DealerLocatorSearchForm
              location={location}
              setLocation={setLocation}
              getLocations={getLocations}
              setNumDisplayed={setNumDisplayed}
              autoSearch={autoSearch}
              isSearchLoading={isLoading}
              isModal={isModal}
              resetLocationsAndMapToggle={resetLocationsAndMapToggle}
              resetFilters={resetFilters}
              setIsSelectStoreError={setIsSelectStoreError}
              storeAddress={storeAddress}
            />
            <DealerLocatorOptionsWrapper
              locations={locations}
              showInCountryToggle={showInCountryToggle}
              onSearchThisAreaClick={onSearchThisAreaClick}
              isMapToggleChecked={isMapToggleChecked}
              setIsMapToggleChecked={setIsMapToggleChecked}
              isExtendMapToggleChecked={isExtendMapToggleChecked}
              setIsExtendMapToggleChecked={setIsExtendMapToggleChecked}
              isLoading={isLoading}
              filters={filters}
              areFiltersLoading={areFiltersLoading}
              setAreFiltersLoading={setAreFiltersLoading}
              isGetLocationsError={isError}
              showNoStoresMapErrorMessage={showNoStoresMapErrorMessage}
              showNetworkErrorMessage={showNetworkErrorMessage}
              setShouldCloseEntireDL={setShouldCloseEntireDL}
              filtersDrawer={filtersDrawer}
              allFilteredDealerStoresByDistance={
                allFilteredDealerStoresByDistance
              }
              filteredAndOrderedVisibleDealerStores={
                filteredAndOrderedVisibleDealerStores
              }
              isHidingMaps={isHidingMaps}
            />
          </div>
          <form
            action={selectStoreWithParams}
            id={formId}
            onSubmit={handleSubmit}
          >
            <DealerLocatorSearchResults
              numDisplayed={numDisplayed}
              setNumDisplayed={setNumDisplayed}
              unitOfMeasure={unitOfMeasure}
              isMapToggleChecked={isMapToggleChecked}
              isLoading={isLoading}
              selectStoreState={selectStoreState}
              showFilterErrorMessage={showFilterErrorMessage}
              showNoStoresMapErrorMessage={
                showNoStoresMapErrorMessage && !isMapToggleChecked
              }
              showNetworkErrorMessage={
                showNetworkErrorMessage && !isMapToggleChecked
              }
              allFilteredDealerStoresByDistance={
                allFilteredDealerStoresByDistance
              }
              filteredAndOrderedVisibleDealerStores={
                filteredAndOrderedVisibleDealerStores
              }
              isSelectStoreError={isSelectStoreError}
              isStoreLocationTandCFlag={isStoreLocationTandCFlag}
              setIsSelectStoreError={setIsSelectStoreError}
              selectStoreAction={selectStoreAction}
            />
          </form>
        </DrawerBody>
        <DealerLocatorFooter
          hasSearchedForLocations={!isIdle}
          isSearchingForLocations={isLoading}
          isStoreLocationTandCFlag={isStoreLocationTandCFlag}
          showNoStoresMapErrorMessage={showNoStoresMapErrorMessage}
          showNetworkErrorMessage={showNetworkErrorMessage}
          isModal={isModal}
          formId={formId}
          locations={locations}
          isSelectStoreError={isSelectStoreError}
          isWelcomeModal={isWelcomeModal}
          selectStoreState={selectStoreState}
        />
      </FormProvider>
      <FormProvider {...filterMethods}>
        <DealerLocatorFiltersDrawer
          defaultFilterOptionsValues={defaultFilterOptionsValues}
          filtersDrawer={filtersDrawer}
          filters={filters}
          resetStoreId={resetStoreId}
          setAreFiltersLoading={setAreFiltersLoading}
          setFilters={setFilters}
          setShouldCloseEntireDL={setShouldCloseEntireDL}
          showFilterErrorMessage={showFilterErrorMessage}
          sortingOptions={sortingOptions}
        />
      </FormProvider>
    </>
  );
};
